import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { type Participant } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
import { deferDatabaseEntryProjection } from '@databases/redux/collab/actions';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  getBackendAgentDeletedEntryIds,
  isEntryOrderingChangeDeletionOnly
} from '../backend-agent-entry-deletions.ts';
import { getBackendAgentStreamingParticipant } from '../backend-agent-streaming-participant.ts';

export const PROGRESSIVE_ENTRY_DELETE_REVEAL_DELAY_MS = 700;

const MAX_REVEAL_TICKS = 4;

type DatabaseTransaction = Parameters<CollabManager['database']['getDiff']>[0];

interface QueuedEntryDelete {
  entryId: string;
  participant: Participant;
}

interface UseProgressiveEntryDeleteRevealOptions {
  collabManager: CollabManager | undefined;
  contentId: string;
  entryIds: readonly string[];
  isPaused: boolean;
  onEligibleBackendAgentEntryDelete?: () => void;
  resetKey: string;
}

export type ProgressiveEntryDeleteRevealResult =
  | { kind: 'idle' }
  | { activeEntryId: string; kind: 'revealing'; participant: Participant };

export function useProgressiveEntryDeleteReveal({
  collabManager,
  contentId,
  entryIds,
  isPaused,
  onEligibleBackendAgentEntryDelete,
  resetKey
}: UseProgressiveEntryDeleteRevealOptions): ProgressiveEntryDeleteRevealResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const dispatch = defaultStoreUtils.useDispatch();
  const previousEntryIds = useRef<readonly string[]>([]);
  const visibleEntryIds = useRef<ReadonlySet<string>>(new Set(entryIds));
  visibleEntryIds.current = new Set(entryIds);
  const revealBatchSize = useRef(1);
  const activeBatchSize = useRef(1);
  const queuedEntryDeletesRef = useRef<readonly QueuedEntryDelete[]>([]);
  const [queuedEntryDeletes, setQueuedEntryDeletes] = useState<
    readonly QueuedEntryDelete[]
  >([]);
  const activeEntryDelete =
    queuedEntryDeletes[Math.min(activeBatchSize.current, queuedEntryDeletes.length) - 1];

  const replaceQueuedEntryDeletes = useCallback(
    (nextQueuedEntryDeletes: readonly QueuedEntryDelete[]) => {
      queuedEntryDeletesRef.current = nextQueuedEntryDeletes;
      if (nextQueuedEntryDeletes.length === 0) {
        revealBatchSize.current = 1;
        activeBatchSize.current = 1;
      }
      setQueuedEntryDeletes(nextQueuedEntryDeletes);
    },
    []
  );

  const projectEntryDeletions = useCallback(
    (entryIds: readonly string[]) => {
      if (!collabManager || entryIds.length === 0) {
        return;
      }

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [],
            changedEntryIds: [...entryIds],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false
          },
          collabDatabase: collabManager.database,
          isReadOnly: collabManager.isReadOnly
        })
      );
    },
    [collabManager, contentId, dispatch]
  );

  useLayoutEffect(() => {
    queuedEntryDeletesRef.current = [];
    revealBatchSize.current = 1;
    activeBatchSize.current = 1;
    setQueuedEntryDeletes([]);

    if (!collabManager) {
      previousEntryIds.current = [];
      return;
    }

    previousEntryIds.current = collabManager.database.entries;
    const onAfterTransaction = (transaction: DatabaseTransaction) => {
      const diff = collabManager.database.getDiff(transaction);
      const currentEntryIds = collabManager.database.entries;
      const previousTransactionEntryIds = previousEntryIds.current;
      previousEntryIds.current = currentEntryIds;

      if (!diff.entriesOrderingChanged) {
        return;
      }

      const deletedEntryIds = getBackendAgentDeletedEntryIds({
        currentEntryIds,
        previousEntryIds: previousTransactionEntryIds,
        transaction
      });

      if (
        deletedEntryIds.length === 0 ||
        !isEntryOrderingChangeDeletionOnly({
          currentEntryIds,
          deletedEntryIds,
          previousEntryIds: previousTransactionEntryIds
        })
      ) {
        if (queuedEntryDeletesRef.current.length > 0) {
          projectEntryDeletions(
            queuedEntryDeletesRef.current.map(({ entryId }) => entryId)
          );
          replaceQueuedEntryDeletes([]);
        }
        return;
      }

      const visibleEntryDeletes = deletedEntryIds.filter((entryId) =>
        visibleEntryIds.current.has(entryId)
      );
      if (visibleEntryDeletes.length === 0) {
        return;
      }

      onEligibleBackendAgentEntryDelete?.();
      if (!isDatabaseBEStreamingEnabled) {
        return;
      }

      deferDatabaseEntryProjection(transaction, visibleEntryDeletes, {
        deferEntriesOrdering: true
      });
      const participant = getBackendAgentStreamingParticipant(transaction, collabManager);
      const nextQueuedEntryDeletes = [
        ...queuedEntryDeletesRef.current,
        ...visibleEntryDeletes.map((entryId) => ({ entryId, participant }))
      ];
      revealBatchSize.current = Math.max(
        revealBatchSize.current,
        Math.ceil((nextQueuedEntryDeletes.length - 1) / MAX_REVEAL_TICKS)
      );
      if (queuedEntryDeletesRef.current.length === 0) {
        activeBatchSize.current = 1;
      }
      replaceQueuedEntryDeletes(nextQueuedEntryDeletes);
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
      projectEntryDeletions(queuedEntryDeletesRef.current.map(({ entryId }) => entryId));
      queuedEntryDeletesRef.current = [];
    };
  }, [
    collabManager,
    isDatabaseBEStreamingEnabled,
    onEligibleBackendAgentEntryDelete,
    projectEntryDeletions,
    replaceQueuedEntryDeletes,
    resetKey
  ]);

  useEffect(() => {
    if (!activeEntryDelete || isPaused) {
      return;
    }

    const timeout = setTimeout(() => {
      const batchSize = activeBatchSize.current;
      const currentQueuedEntryDeletes = queuedEntryDeletesRef.current;
      projectEntryDeletions(
        currentQueuedEntryDeletes.slice(0, batchSize).map(({ entryId }) => entryId)
      );
      const nextQueuedEntryDeletes = currentQueuedEntryDeletes.slice(batchSize);
      activeBatchSize.current = Math.min(
        revealBatchSize.current,
        nextQueuedEntryDeletes.length
      );
      replaceQueuedEntryDeletes(nextQueuedEntryDeletes);
    }, PROGRESSIVE_ENTRY_DELETE_REVEAL_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [activeEntryDelete, isPaused, projectEntryDeletions, replaceQueuedEntryDeletes]);

  return activeEntryDelete
    ? {
        activeEntryId: activeEntryDelete.entryId,
        kind: 'revealing',
        participant: activeEntryDelete.participant
      }
    : { kind: 'idle' };
}
